import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createRecurring,
  deleteRecurring,
  getCategories,
  getRecurring,
  updateRecurring,
  type Category,
  type RecurringTemplate,
} from '../api';
import { CategoryPicker } from '../components/CategoryPicker';
import { CategoryIcon } from '../categoryIcons';
import { amountToInputString, eur, parseAmountInput } from '../utils/money';

export default function RecurringPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formBusy, setFormBusy] = useState(false);
  const [formErr, setFormErr] = useState('');

  const pickerCategories = useMemo(
    () =>
      categories.slice().sort((a, b) => {
        const ta = a.type === 'in' ? 0 : 1;
        const tb = b.type === 'in' ? 0 : 1;
        if (ta !== tb) return ta - tb;
        return a.name.localeCompare(b.name, 'it');
      }),
    [categories]
  );

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categories) m.set(c.categoryId, c);
    return m;
  }, [categories]);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const [catRes, recRes] = await Promise.all([getCategories(), getRecurring()]);
      setCategories(catRes.categories);
      setTemplates(recRes.templates);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openAdd() {
    setEditId(null);
    setFormName('');
    setFormAmount('');
    setFormErr('');
    const first = pickerCategories[0];
    setFormCategoryId(first?.categoryId ?? '');
    setFormOpen(true);
  }

  function openEdit(t: RecurringTemplate) {
    setEditId(t.recurringId);
    setFormName(t.name);
    setFormCategoryId(t.categoryId);
    setFormAmount(amountToInputString(t.defaultAmount));
    setFormErr('');
    setFormOpen(true);
  }

  async function onSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    setFormErr('');
    const name = formName.trim();
    if (!name) {
      setFormErr('Nome obbligatorio');
      return;
    }
    if (!formCategoryId) {
      setFormErr('Seleziona una categoria');
      return;
    }
    const amount = parseAmountInput(formAmount);
    if (amount == null) {
      setFormErr('Importo predefinito non valido');
      return;
    }

    setFormBusy(true);
    try {
      if (editId) {
        await updateRecurring(editId, {
          name,
          categoryId: formCategoryId,
          defaultAmount: amount,
        });
      } else {
        await createRecurring({
          name,
          categoryId: formCategoryId,
          defaultAmount: amount,
        });
      }
      setFormOpen(false);
      await load();
    } catch (x) {
      setFormErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setFormBusy(false);
    }
  }

  async function onToggleActive(t: RecurringTemplate) {
    setErr('');
    try {
      await updateRecurring(t.recurringId, { active: !t.active });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  async function onDelete(t: RecurringTemplate) {
    if (!confirm(`Eliminare «${t.name}» dall'anagrafica? Le voci già inserite nei mesi restano in elenco.`)) {
      return;
    }
    setErr('');
    try {
      await deleteRecurring(t.recurringId);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore');
    }
  }

  return (
    <div className="recurring-page">
      <p className="recurring-back">
        <Link to="/">← Spese</Link>
      </p>
      <h2 className="page-title">Movimenti ricorrenti</h2>
      <p className="home-muted recurring-page-lead">
        Definisci entrate e uscite fisse mensili. Su ogni mese le inserisci dall&apos;icona stella nella home.
      </p>

      <button type="button" className="btn-primary recurring-add-btn" onClick={openAdd} disabled={loading}>
        <Plus size={18} strokeWidth={2} aria-hidden />
        Nuovo movimento
      </button>

      {err && <p className="home-err">{err}</p>}

      {loading ? (
        <p className="home-muted">Caricamento…</p>
      ) : templates.length === 0 ? (
        <p className="home-muted">Nessun movimento ricorrente. Aggiungine uno per iniziare.</p>
      ) : (
        <ul className="recurring-catalog-list">
          {templates.map((t) => {
            const cat = categoryById.get(t.categoryId);
            const isIn = cat?.type === 'in';
            return (
              <li key={t.recurringId} className={`recurring-catalog-row ${t.active ? '' : 'inactive'}`}>
                <CategoryIcon iconId={cat?.icon} size={24} className="recurring-catalog-icon" />
                <div className="recurring-catalog-main">
                  <span className="recurring-catalog-name">{t.name}</span>
                  <span className="recurring-catalog-meta">
                    <span className={`category-picker-pill category-picker-pill--${isIn ? 'in' : 'out'}`}>
                      {isIn ? 'Entrata' : 'Uscita'}
                    </span>
                    {' · '}
                    {cat?.name ?? t.categoryId} · {isIn ? '+' : '−'}
                    {eur(Math.abs(t.defaultAmount))}
                  </span>
                </div>
                <label className="recurring-active-toggle">
                  <input
                    type="checkbox"
                    checked={t.active}
                    onChange={() => void onToggleActive(t)}
                    aria-label={`Attiva ${t.name}`}
                  />
                  <span>Attiva</span>
                </label>
                <div className="recurring-catalog-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => openEdit(t)}
                    aria-label={`Modifica ${t.name}`}
                  >
                    <Pencil size={17} strokeWidth={2} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="icon-btn icon-btn--danger"
                    onClick={() => void onDelete(t)}
                    aria-label={`Elimina ${t.name}`}
                  >
                    <Trash2 size={17} strokeWidth={2} aria-hidden />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {formOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget && !formBusy) setFormOpen(false);
          }}
        >
          <div className="modal-panel" role="dialog" aria-labelledby="recurring-form-title">
            <h3 id="recurring-form-title" className="modal-title">
              {editId ? 'Modifica movimento' : 'Nuovo movimento ricorrente'}
            </h3>
            <form onSubmit={(e) => void onSubmitForm(e)}>
              <div className="field">
                <label htmlFor="rec-name">Nome / descrizione</label>
                <input
                  id="rec-name"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="es. Stipendio, Bolletta luce"
                  maxLength={200}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="rec-cat">Categoria</label>
                <CategoryPicker
                  id="rec-cat"
                  categories={pickerCategories}
                  value={formCategoryId}
                  onChange={setFormCategoryId}
                />
              </div>
              <div className="field">
                <label htmlFor="rec-amount">Importo predefinito (€)</label>
                <input
                  id="rec-amount"
                  type="text"
                  inputMode="decimal"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  placeholder="es. 85,00"
                  required
                />
              </div>
              {formErr && <p className="field-err">{formErr}</p>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setFormOpen(false)}
                  disabled={formBusy}
                >
                  Annulla
                </button>
                <button type="submit" className="btn-primary btn-primary-inline" disabled={formBusy}>
                  {formBusy ? '…' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
